/**
 * Coerce any axios/fetch error into a string suitable for toast.error().
 *
 * FastAPI returns errors in two shapes:
 *   1. HTTPException(detail="some string")          → detail is a string
 *   2. Pydantic validation errors                   → detail is an array of
 *      {type, loc, msg, input, url} objects
 *   3. HTTPException(detail={"error": ..., ...})    → detail is an object
 *
 * Sonner's toast.error renders its argument as a React child, so passing in
 * an array or object causes "Objects are not valid as a React child".
 */
export function formatErrorDetail(err, fallback = "Something went wrong") {
  // Allow callers to pass the detail directly OR the whole error object
  const detail = err?.response?.data?.detail !== undefined
    ? err.response.data.detail
    : err?.response?.data?.error !== undefined
      ? err.response.data.error
      : err;

  if (typeof detail === "string") return detail;

  // Pydantic validation: array of {type, loc, msg, ...}
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const where = Array.isArray(d?.loc) ? d.loc.slice(1).join(".") : null;
        const msg = d?.msg || JSON.stringify(d);
        return where ? `${where}: ${msg}` : msg;
      })
      .join("; ");
  }

  // HTTPException with object detail (e.g. our HubSpot guard responses)
  if (detail && typeof detail === "object") {
    if (typeof detail.error === "string") return detail.error;
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }

  // Plain Error instance
  if (err?.message) return err.message;

  return fallback;
}
